import * as XLSX from 'xlsx';
import type { MRT_Row, MRT_TableInstance } from 'mantine-react-table';

import {
  DELETED_COLUMN_HEADER,
  formatDeletedExportValue,
} from './excelDeletedColumn';

export const EXISTING_RECORD_ID_HEADER = 'Existing record ID';

export const EXISTING_RECORD_ID_HEADERS = [
  EXISTING_RECORD_ID_HEADER,
  'ID',
  'id',
  'uuid',
  'UUID',
] as const;

const DEFAULT_ID_HEADERS: string[] = [...EXISTING_RECORD_ID_HEADERS];

export type TableExportOptions = {
  includeIds: boolean;
  includeDeletedColumn: boolean;
};

export type TableExportConfig<T extends Record<string, unknown>> = {
  idField?: keyof T & string;
  idHeader?: string;
  filename: string;
  sheetName: string;
  getCellValue?: (row: T, columnId: string) => unknown;
  getDeletedValue?: (row: T) => boolean | undefined;
};

function formatCellValue(value: unknown): string | number | boolean {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return value as string | number | boolean;
}

function columnHeaderLabel(columnDef: { header?: unknown }, columnId: string): string {
  const header = columnDef.header;
  if (typeof header === 'string') return header;
  return columnId;
}

function resolveCellValue<T extends Record<string, unknown>>(
  mrtRow: MRT_Row<T>,
  columnId: string,
  columnDef: { meta?: { exportValue?: (row: T) => unknown } },
  getCellValue?: TableExportConfig<T>['getCellValue'],
): unknown {
  if (getCellValue) {
    return getCellValue(mrtRow.original, columnId);
  }
  const meta = columnDef.meta;
  if (meta?.exportValue) {
    return meta.exportValue(mrtRow.original);
  }
  return mrtRow.getValue(columnId);
}

function isExportableColumn(columnId: string): boolean {
  return !columnId.startsWith('mrt-row-');
}

export type SerializedTableExport = {
  headers: string[];
  rows: unknown[][];
};

export function serializeMrtTableForExport<T extends Record<string, unknown>>(
  table: MRT_TableInstance<T>,
  exportOptions: TableExportOptions,
  config: Pick<
    TableExportConfig<T>,
    'idField' | 'idHeader' | 'getCellValue' | 'getDeletedValue'
  >,
): SerializedTableExport | null {
  const filteredRows = table.getPrePaginationRowModel().rows;
  if (filteredRows.length === 0) {
    return null;
  }

  const visibleColumns = table
    .getVisibleLeafColumns()
    .filter((column) => isExportableColumn(column.id));

  const headers = visibleColumns.map((column) =>
    columnHeaderLabel(column.columnDef, column.id),
  );

  if (exportOptions.includeIds && config.idField) {
    headers.unshift(config.idHeader ?? EXISTING_RECORD_ID_HEADER);
  }

  if (exportOptions.includeDeletedColumn) {
    headers.push(DELETED_COLUMN_HEADER);
  }

  const rows = filteredRows.map((mrtRow) => {
    const values = visibleColumns.map((column) =>
      formatCellValue(
        resolveCellValue(mrtRow, column.id, column.columnDef, config.getCellValue),
      ),
    );
    if (exportOptions.includeIds && config.idField) {
      values.unshift(formatCellValue(mrtRow.original[config.idField]));
    }
    if (exportOptions.includeDeletedColumn) {
      const isDeleted = config.getDeletedValue?.(mrtRow.original);
      values.push(formatDeletedExportValue(isDeleted));
    }
    return values;
  });

  return { headers, rows };
}

export function buildExcelArrayBuffer(
  worksheetData: unknown[][],
  sheetName: string,
): ArrayBuffer {
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

export function exportMrtTableToExcel<T extends Record<string, unknown>>(
  table: MRT_TableInstance<T>,
  exportOptions: TableExportOptions,
  config: TableExportConfig<T>,
): boolean {
  const serialized = serializeMrtTableForExport(table, exportOptions, config);
  if (!serialized) {
    return false;
  }

  downloadExcelSheet(
    [serialized.headers, ...serialized.rows],
    config.filename,
    config.sheetName,
  );
  return true;
}

export function exportRowsToExcel(
  headers: string[],
  rows: unknown[][],
  filename: string,
  sheetName: string,
): void {
  downloadExcelSheet([headers, ...rows], filename, sheetName);
}

function downloadExcelSheet(
  worksheetData: unknown[][],
  filename: string,
  sheetName: string,
): void {
  const wbout = buildExcelArrayBuffer(worksheetData, sheetName);
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function readExcelFile(file: File): Promise<Record<string, unknown>[]> {
  return file.arrayBuffer().then((data) => {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet) as Record<string, unknown>[];
  });
}

export function isIdColumnKey(key: string): boolean {
  return EXISTING_RECORD_ID_HEADERS.includes(key as (typeof EXISTING_RECORD_ID_HEADERS)[number]);
}

export function pickRowId(
  row: Record<string, unknown>,
  idHeaders: string[] = DEFAULT_ID_HEADERS,
): string | null {
  for (const header of idHeaders) {
    const value = row[header];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return null;
}

export type ImportSplitResult = {
  creates: Record<string, unknown>[];
  updates: Array<{ id: string; row: Record<string, unknown> }>;
  missingIds: string[];
};

export function splitImportRowsById(
  rows: Record<string, unknown>[],
  existingIds: Set<string>,
  idHeaders: string[] = DEFAULT_ID_HEADERS,
): ImportSplitResult {
  const creates: Record<string, unknown>[] = [];
  const updates: Array<{ id: string; row: Record<string, unknown> }> = [];
  const missingIds: string[] = [];

  for (const row of rows) {
    const id = pickRowId(row, idHeaders);
    if (id) {
      if (existingIds.has(id)) {
        updates.push({ id, row });
      } else {
        missingIds.push(id);
      }
      continue;
    }
    creates.push(row);
  }

  return { creates, updates, missingIds };
}

export function datedExportFilename(prefix: string): string {
  const dateStamp = new Date().toISOString().slice(0, 10);
  return `${prefix}_${dateStamp}.xlsx`;
}
